import { DefaultButton } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useMemo, useState } from "react";

import styles from "./LoginButton.module.css";
import {
    appServicesLogout,
    auth0Config,
    checkLoggedIn,
    getUsername,
    isUsingAppServicesLogin,
    useLogin
} from "../../authConfig";
import { LoginContext } from "../../loginContext";
import { useAuthClient } from "../../useAuthClient";

const LOGOUT_HARD_REFRESH_PARAM = "logoutHardRefresh";
const resolveAuth0LogoutEndpoint = (): string | undefined => {
    if (auth0Config.logoutUrl && auth0Config.logoutUrl.trim().length > 0) {
        return auth0Config.logoutUrl;
    }
    if (auth0Config.domain && auth0Config.domain.trim().length > 0) {
        return `https://${auth0Config.domain}/v2/logout`;
    }
    return undefined;
};

const clearClientAuthState = () => {
    try {
        sessionStorage.clear();
    } catch (error) {
        console.warn("[Auth] Failed to clear sessionStorage during logout", error);
    }

    try {
        localStorage.clear();
    } catch (error) {
        console.warn("[Auth] Failed to clear localStorage during logout", error);
    }

    try {
        const cookies = document.cookie ? document.cookie.split(";") : [];
        const expiry = "Thu, 01 Jan 1970 00:00:00 GMT";
        const hostname = window.location.hostname;
        cookies.forEach(cookie => {
            const name = cookie.split("=")[0]?.trim();
            if (!name) {
                return;
            }
            document.cookie = `${name}=;expires=${expiry};path=/`;
            if (hostname.includes(".")) {
                document.cookie = `${name}=;expires=${expiry};path=/;domain=.${hostname}`;
            }
        });
    } catch (error) {
        console.warn("[Auth] Failed to clear cookies during logout", error);
    }
};

const buildLogoutReturnTo = (): string => {
    const returnUrl = new URL(window.location.origin);
    returnUrl.searchParams.set(LOGOUT_HARD_REFRESH_PARAM, Date.now().toString());
    return returnUrl.toString();
};

export const LoginButton = () => {
    const auth = useAuthClient();
    const { loggedIn, setLoggedIn } = useContext(LoginContext);
    const [username, setUsername] = useState("");
    const { t } = useTranslation();

    useEffect(() => {
        const syncState = async () => {
            if (!useLogin || !auth) {
                return;
            }
            setLoggedIn(await checkLoggedIn(auth));
            setUsername((await getUsername(auth)) ?? "");
        };

        if (!auth?.isLoading) {
            syncState();
        }
    }, [auth, setLoggedIn]);

    if (!useLogin || !auth) {
        return null;
    }

    const { isAuthenticated, loginWithRedirect, logout } = auth;
    const auth0LogoutEndpoint = useMemo(resolveAuth0LogoutEndpoint, []);

    const displayName = useMemo(() => {
        if (username.length > 0) {
            return username;
        }
        return t("login");
    }, [t, username]);

    const handleLogin = async () => {
        try {
            console.log("[Auth] Initiating Auth0 login redirect");
            await loginWithRedirect();
        } catch (error) {
            console.error(error);
        }
    };

    const handleLogout = async () => {
        clearClientAuthState();

        const returnTo = buildLogoutReturnTo();

        if (isUsingAppServicesLogin && !isAuthenticated) {
            console.log("[Auth] Logging out via App Services logout endpoint (.auth/logout)");
            appServicesLogout(returnTo);
            return;
        }

        try {
            if (auth0LogoutEndpoint) {
                const logoutUrl = new URL(auth0LogoutEndpoint);
                logoutUrl.searchParams.set("returnTo", returnTo);
                if (auth0Config.clientId) {
                    logoutUrl.searchParams.set("client_id", auth0Config.clientId);
                }

                console.log("[Auth] Redirecting to Auth0 logout endpoint", { logoutUrl: logoutUrl.toString() });
                window.location.replace(logoutUrl.toString());
                return;
            }

            console.warn("[Auth] No Auth0 logout endpoint configured, using Auth0 SDK logout");
            logout?.({
                logoutParams: {
                    returnTo
                }
            });
        } catch (error) {
            console.error(error);
        }
    };

    if (!loggedIn) {
        return (
            <DefaultButton className={styles.loginButton} text={t("login")} onClick={handleLogin}></DefaultButton>
        );
    }

    return (
        <div className={styles.authenticatedUser}>
            <span className={styles.username}>{displayName}</span>
            <DefaultButton className={styles.logoutButton} text={t("logout")} onClick={handleLogout}></DefaultButton>
        </div>
    );
};
